import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, normalizeClass as _normalizeClass, createVNode as _createVNode, createCommentVNode as _createCommentVNode, withCtx as _withCtx, createBlock as _createBlock } from "vue"

const _hoisted_1 = /*#__PURE__*/_createElementVNode("hr", { class: "mt-2" }, null, -1)
const _hoisted_2 = { class: "flex items-center gap-4" }
const _hoisted_3 = ["onClick"]
const _hoisted_4 = ["disabled"]

import { PlusIcon, XMarkIcon } from "@heroicons/vue/24/solid";
import { FieldArray, Form, SubmissionHandler } from "vee-validate";
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import * as yup from "yup";
import LoadingSpinner from "shared/components/loading_state/LoadingSpinner.vue";
import Modal from "shared/components/modals/Modal.vue";
import { companyTypes } from "shared/constants/user";
import { User, UserCompanyType } from "shared/types/User";
import InputField from "@/components/forms/InputField.vue";
import SelectListField from "@/components/forms/SelectListField.vue";
import { useBulkInviteUsersToProject, useBulkInviteUsersToOrganization } from "@/composables/user";

type InviteUsersFormValues = {
  users: { email: string; company_type: UserCompanyType }[];
};


export default /*#__PURE__*/_defineComponent({
  __name: 'InviteUsersModal',
  props: {
    existingUsers: {},
    organizationId: {}
  },
  emits: ["close"],
  setup(__props: any, { emit: __emit }) {

const props = __props;
const emit = __emit;

const { t } = useI18n();
const { bulkInviteUsersToProject } = useBulkInviteUsersToProject();
const { bulkInviteUsersToOrganization } = useBulkInviteUsersToOrganization();

const companyTypesList = companyTypes.map((type) => ({
  value: type,
  name: t(`admin.project_participants.company_types.${type}`),
}));

const inviteSchema = yup.object().shape({
  users: yup.array().of(
    yup.object().shape({
      email: yup
        .string()
        .email(t("admin.project_participants.modal_invite_user.error_mail_invalid"))
        .required(t("admin.project_participants.modal_invite_user.error_mail_required")),
      company_type: yup.string().required(t("authentication.validation.validation_field_required")),
    }),
  ),
});

const initialValues = computed(() => ({
  users: [{ email: "", company_type: "" }],
}));

const handleSubmitInviteUsers: SubmissionHandler = async (genericObject) => {
  const existingUserEmails = new Set(props.existingUsers.map((user) => user.email));
  const values = genericObject as InviteUsersFormValues;
  const usersToInvite = values.users
    .map((item) => ({
      email: item.email.replace(/\s/g, "").toLowerCase(), // sanitize
      company_type: item.company_type,
    }))
    .filter((user) => !existingUserEmails.has(user.email));

  if (usersToInvite.length > 0) {
    if (props.organizationId) {
      await bulkInviteUsersToOrganization({
        usersToInvite,
        organizationId: props.organizationId,
      })
        .then(() => {
          emit("close");
        })
        .catch(() => undefined);
    } else {
      await bulkInviteUsersToProject(usersToInvite)
        .then(() => {
          emit("close");
        })
        .catch(() => undefined);
    }
  } else {
    emit("close");
  }
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(Modal, {
    open: true,
    onClose: _cache[0] || (_cache[0] = ($event: any) => (emit('close'))),
    "custom-cls": "sm:w-full lg:w-[650px]"
  }, {
    title: _withCtx(() => [
      _createTextVNode(_toDisplayString(_unref(t)("admin.project_participants.modal_invite_user.invite_users")) + " ", 1),
      _hoisted_1
    ]),
    content: _withCtx(() => [
      _createVNode(_unref(Form), {
        onSubmit: handleSubmitInviteUsers,
        initialValues: initialValues.value,
        validationSchema: _unref(inviteSchema),
        class: "flex flex-col gap-2 w-full"
      }, {
        default: _withCtx(({ isSubmitting, errors }) => [
          _createVNode(_unref(FieldArray), { name: "users" }, {
            default: _withCtx(({ fields, push, remove }) => [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(fields, (field, idx) => {
                return (_openBlock(), _createElementBlock("div", { key: idx }, [
                  _createElementVNode("div", _hoisted_2, [
                    _createVNode(InputField, {
                      name: `users[${idx}].email`,
                      label: idx === 0 ? _unref(t)('admin.project_participants.email') : '',
                      class: _normalizeClass(["w-[250px]", {
                  'mb-[19px]': errors[`users[${idx}].company_type`],
                }])
                    }, null, 8, ["name", "label", "class"]),
                    _createVNode(SelectListField, {
                      name: `users[${idx}].company_type`,
                      label: idx === 0 ? _unref(t)('admin.project_participants.company_type') : '',
                      options: _unref(companyTypesList),
                      minWidth: 250,
                      placeholder: `--
              ${_unref(t)('form.select_option')} --`,
                      class: _normalizeClass({
                  'mb-[19px]': errors[`users[${idx}].email`],
                })
                    }, null, 8, ["name", "label", "options", "placeholder", "class"]),
                    (fields.length > 1)
                      ? (_openBlock(), _createElementBlock("button", {
                          key: 0,
                          type: "button",
                          class: _normalizeClass({
                  'mt-5': idx === 0,
                  'mb-[19px]':
                    errors[`users[${idx}].email`] || errors[`users[${idx}].company_type`],
                })
                        }, [
                          _createVNode(_unref(XMarkIcon), {
                            class: "h-5 text-red-900 hover:text-red",
                            onClick: ($event: any) => (remove(idx))
                          }, null, 8, ["onClick"])
                        ], 2))
                      : _createCommentVNode("", true)
                  ])
                ]))
              }), 128)),
              _createElementVNode("div", null, [
                _createElementVNode("button", {
                  type: "button",
                  class: "inline-flex items-center justify-center text-xs mt-3",
                  onClick: ($event: any) => (push({ email: '', company_type: '' }))
                }, [
                  _createVNode(_unref(PlusIcon), {
                    class: "h-4 w-4",
                    "aria-hidden": "true"
                  }),
                  _createTextVNode(" " + _toDisplayString(_unref(t)("admin.project_participants.modal_invite_user.add_user")), 1)
                ], 8, _hoisted_3)
              ])
            ]),
            _: 2
          }, 1024),
          _createElementVNode("button", {
            type: "submit",
            class: "w-min self-end focus:outline-none flex justify-center rounded-md border border-transparent bg-yellow-500 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-yellow-600 focus:ring-0 disabled:cursor-default disabled:bg-gray-300",
            disabled: isSubmitting
          }, [
            isSubmitting
              ? (_openBlock(), _createBlock(LoadingSpinner, {
                  key: 0,
                  size: "w-5 h-5",
                  color: "white",
                  class: "mr-2"
                }))
              : _createCommentVNode("", true),
            _createTextVNode(" " + _toDisplayString(_unref(t)("admin.project_participants.modal_invite_user.invite")), 1)
          ], 8, _hoisted_4)
        ]),
        _: 1
      }, 8, ["initialValues", "validationSchema"])
    ]),
    _: 1
  }))
}
}

})